/**
 * The ONE pairing engine of the home dashboards (KAN-1003, flex in KAN-1004). A role home lists the cards it
 * ACTUALLY renders (hidden / stepped-out cards already removed), each tagged `kind: 'half' | 'strip' | 'flex'`,
 * and gets back the 12-column span of each:
 * - halves pair 6 / 6 in order;
 * - a half with no partner before the next strip (or at the end of the list) takes 12;
 * - a strip always takes 12;
 * - the FLEX card (the Next 7 days band) fills whichever hole the halves leave. If the number of halves is ODD the
 *   flex card becomes a HALF and pairs with the lone one, so no card is left alone on a row; if it is EVEN the
 *   flex card is a STRIP (12). It keeps its own position when that already works (it sits inside the run of
 *   halves it pairs into, or after all of them); otherwise it moves right after the lone half (odd) or after the
 *   run of halves it would have split (even).
 * Web places the spans in the 12-column grid; tablet places them in its 2-column grid (6 = one column,
 * 12 = both); phone is one column, ignores spans and keeps the order it was given.
 */

const isHalf = (item) => item.kind === 'half';

/**
 * Resolve the flex card: returns the cards in RENDERING order, each `{ card, index, kind: 'half' | 'strip' }`
 * (`index` = position in the input). No flex card -> the input order, unchanged.
 */
export function resolveFlex(cards) {
  const list = Array.isArray(cards) ? cards : [];
  const items = list.map((card, index) => ({ card, index, kind: card?.kind === 'half' ? 'half' : 'strip' }));
  const flexAt = list.findIndex((card) => card?.kind === 'flex');
  if (flexAt < 0) return items;

  const flex = items[flexAt];
  const base = items.filter((item) => item !== flex);
  // Runs of consecutive halves, as [start, end] positions in `base`.
  const runs = [];
  base.forEach((item, i) => {
    if (!isHalf(item)) return;
    const last = runs[runs.length - 1];
    if (last && last[1] === i - 1) last[1] = i;
    else runs.push([i, i]);
  });
  const halves = base.filter(isHalf).length;
  const slot = flexAt; // where the flex card sits among the others

  let at = slot;
  if (halves % 2 === 1) {
    // ODD: a half. Join the last run with a lone half: stay where we are if inside it, else next to it.
    const [start, end] = [...runs].reverse().find(([s, e]) => (e - s + 1) % 2 === 1);
    flex.kind = 'half';
    if (slot <= start) at = start;
    else if (slot > end) at = end + 1;
  } else {
    // EVEN: a strip. Never split a run of halves - go after it.
    const inside = runs.find(([s, e]) => slot > s && slot <= e);
    if (inside) at = inside[1] + 1;
  }
  base.splice(at, 0, flex);
  return base;
}

function spansOf(items) {
  const spans = items.map(() => 12);
  let run = [];
  const flush = () => {
    for (let i = 0; i + 1 < run.length; i += 2) {
      spans[run[i]] = 6;
      spans[run[i + 1]] = 6;
    }
    run = [];
  };
  items.forEach((item, i) => {
    if (isHalf(item)) run.push(i);
    else flush();
  });
  flush();
  return spans;
}

/** Spans aligned with the INPUT list (a flex card gets the span it resolved to). */
export function pairSpans(cards) {
  const items = resolveFlex(cards);
  const placed = spansOf(items);
  const out = [];
  items.forEach((item, i) => { out[item.index] = placed[i]; });
  return out;
}

const liveEntries = (entries) => (Array.isArray(entries) ? entries : []).filter((e) => e && (e.show === undefined || Boolean(e.show)));

/**
 * Same as `pairSpans`, but takes entries `{ key, kind, show }` (entries with a falsy `show` are skipped,
 * so a conditional card never leaves a lone half) and returns `{ [key]: span }`. With `layout === 'phone'`
 * (one column) every span is 12: CSS ignores it there, and it overrides each card's own default span.
 */
export function pairSpanMap(entries, layout) {
  const live = liveEntries(entries);
  const spans = pairSpans(live);
  const map = {};
  live.forEach((e, i) => { map[e.key] = layout === 'phone' ? 12 : spans[i]; });
  return map;
}

/**
 * `pairSpanMap` + the order to RENDER the cards in: `{ spans: { [key]: span }, order: [key, ...] }`. Only live
 * entries are in `order`. A flex card may sit somewhere else than in the input (see the top of this file) on web and
 * tablet; on a phone the order is exactly the input's.
 */
export function pairLayout(entries, layout) {
  const live = liveEntries(entries);
  if (layout === 'phone') {
    return { spans: pairSpanMap(live, layout), order: live.map((e) => e.key) };
  }
  const items = resolveFlex(live);
  const placed = spansOf(items);
  const spans = {};
  items.forEach((item, i) => { spans[item.card.key] = placed[i]; });
  return { spans, order: items.map((item) => item.card.key) };
}

export default pairSpans;
